import { AlertTriangle, Lock, Loader2 } from "lucide-react";

export function PageHeader({ eyebrow, title, subtitle, actions, testid }) {
  return (
    <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between rise" data-testid={testid}>
      <div>
        {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="mt-2 text-sm md:text-base text-muted-foreground max-w-2xl">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Panel({ title, eyebrow, actions, children, className = "", testid, bodyClass = "p-5 md:p-6" }) {
  return (
    <section className={`min-w-0 rounded-lg border bg-card text-card-foreground ${className}`} data-testid={testid}>
      {(title || actions) && (
        <div className="flex items-center justify-between gap-3 border-b px-5 md:px-6 py-4">
          <div>
            {eyebrow && <div className="eyebrow mb-1">{eyebrow}</div>}
            {title && <h2 className="font-heading text-base md:text-lg font-semibold">{title}</h2>}
          </div>
          {actions}
        </div>
      )}
      <div className={bodyClass}>{children}</div>
    </section>
  );
}

export function Loading({ label = "Loading…" }) {
  return (
    <div className="flex items-center gap-2 py-16 justify-center text-muted-foreground" data-testid="loading-state">
      <Loader2 className="h-5 w-5 animate-spin" /> {label}
    </div>
  );
}

export function ErrorState({ message, status }) {
  const denied = status === 403;
  const Icon = denied ? Lock : AlertTriangle;
  return (
    <div className="mx-auto max-w-md py-20 text-center" data-testid={denied ? "access-denied" : "error-state"}>
      <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-accent text-accent-foreground"><Icon className="h-6 w-6" /></div>
      <h2 className="font-heading text-xl font-bold" data-testid="error-message">{message}</h2>
      {denied && <p className="mt-2 text-sm text-muted-foreground">If you think you should see this, ask an EPIL admin.</p>}
    </div>
  );
}

export const VatNote = ({ testid = "vat-note", className = "" }) => <p className={`text-[11px] text-muted-foreground ${className}`} data-testid={testid}>All figures net of VAT unless marked.</p>;

export function StatusBadge({ status }) {
  const map = {
    Live: "bg-[hsl(var(--positive)/0.12)] text-[hsl(var(--positive))]",
    Pipeline: "bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning))]",
    Exited: "bg-muted text-muted-foreground",
  };
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${map[status] || "bg-muted"}`} data-testid="status-badge">{status}</span>;
}

export function PotBadge({ pot }) {
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-bold font-heading tracking-wide ${pot === "AMSM" ? "bg-foreground text-background" : "border border-foreground/30"}`}>{pot}</span>
  );
}

export function Segmented({ options, value, onChange, testid }) {
  return (
    <div className="inline-flex rounded-md border bg-card p-0.5" data-testid={testid}>
      {options.map((o) => (
        <button key={o} onClick={() => onChange(o)} data-testid={`${testid}-${o.toLowerCase()}`}
          className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors duration-150 ${value === o ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
          {o}
        </button>
      ))}
    </div>
  );
}
